import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { Button } from 'primereact/button';

import { useState } from 'react';
import { authAtom, jotaiStore } from '~/atoms/authAtom';

type ConfirmationOption = {
  label: string;
  value: string;
};

export const TriggerConfirmation = ({
  checkpoint,
  confirmationActionText,
  isCompleted,
}: {
  checkpoint: {
    confirmation_options?: ConfirmationOption[];
    confirmation_action?: string;
    checkpoint_id: string;
  };
  confirmationActionText?: string;
  isCompleted: boolean;
}) => {
  const auth = jotaiStore.get(authAtom);
  const [confirmationResult, setConfirmationResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const submitConfirmation = async ({ value }: { value: string }) => {
    const token = await auth?.auth.getToken({
      template: 'banqora_template', // Todo: Maybe move this to env file
    });
    const baseUrl = String(process.env.REACT_EMAIL_AGENT_URL || '');

    const actionPath = checkpoint.confirmation_action;

    const url = `${baseUrl}${actionPath}`;

    return axios.post(
      url,
      {
        confirmation_action: value,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  };

  const confirmationMutation = useMutation({
    mutationFn: submitConfirmation,
    onSuccess: (_, variables) => {
      setConfirmationResult({
        success: true,
        message:
          variables.value === 'ok' ? 'Action confirmed' : 'Action cancelled',
      });
    },
    onError: () => {
      setConfirmationResult({
        success: false,
        message: 'Failed to process confirmation',
      });
    },
  });

  if (!checkpoint.confirmation_options || !checkpoint.confirmation_action) {
    return null;
  }

  if (confirmationResult) {
    return (
      <div
        className="p-3 mt-3 border-round text-center"
        style={{
          backgroundColor: confirmationResult.success ? '#1f3d2e' : '#3d1f1f',
        }}
      >
        <div className="mb-2">
          {confirmationResult.success ? (
            <i className="pi pi-check mr-2" style={{ color: '#6fdea8' }} />
          ) : (
            <i
              className="pi pi-exclamation-triangle mr-2"
              style={{ color: '#de6f6f' }}
            />
          )}
          {confirmationResult.message}
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div
        className="p-3 mt-3 border-round text-center"
        style={{
          backgroundColor: '#1f3d2e',
        }}
      >
        <div className="mb-2">
          <i className="pi pi-check mr-2" style={{ color: '#6fdea8' }} />
          Action completed
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-3 mt-3 border-round"
      style={{ backgroundColor: '#252525' }}
    >
      <div className="mb-2 text-sm">
        {confirmationActionText || 'Confirm this action?'}
      </div>
      <div className="flex gap-2 justify-content-center">
        {checkpoint.confirmation_options.map((option) => (
          <Button
            key={option.value}
            label={option.label}
            severity={option.value === 'ok' ? 'success' : 'danger'}
            size="small"
            disabled={confirmationMutation.isPending}
            onClick={() => confirmationMutation.mutate({ value: option.value })}
            loading={confirmationMutation.isPending}
            outlined
          />
        ))}
      </div>
    </div>
  );
};
